<template>

  <div>

    <!--   compactors filter-->
    <b-card>
      <b-card-title>{{ $t('Compactors filters')}}</b-card-title>
      <b-row>
        <b-col md="6">
          <b-form-group :label="$t('Owners')">
            <v-select multiple :close-on-select="false" v-model="selectedOwners"
                      :dir="$store.getters['auth/preferDirIsRtl'] ? 'rtl' : 'ltr'"
                      :getOptionLabel="o => o.name"
                      :options="ownersFilter"
                      :reduce="o => o.id" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group :label="$t('Contracts')">
            <v-select multiple :close-on-select="false" v-model="selectedContracts"
                      :dir="$store.getters['auth/preferDirIsRtl'] ? 'rtl' : 'ltr'"
                      :getOptionLabel="c => c.name"
                      :options="contractsOptions"
                      :reduce="c => c.id" />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <!--    report form-->
    <b-card>

      <div v-if="loading" class="loading_page">
        <orbit-spinner :animation-duration="1000" :size="40" :color="'#0e96b1'" />
      </div>

      <b-form @submit.prevent="submitReport">
        <b-row>
          <b-col md="12">
          <b-form-group :label="`${$t('Compactors')}`">
            <v-select multiple :close-on-select="false" v-model="request.compactors"
                      :dir="$store.getters['auth/preferDirIsRtl'] ? 'rtl' : 'ltr'" :getOptionLabel="d => d.name"
                      :options="[{ name: $t('all'), device_id: 'all' }, ...compactorsOptions]" :reduce="d => d.device_id" />
            <small v-if="errors && ('compactors' in errors)" class="text-danger">
              {{ $t(errors['compactors'][0]) }}
            </small>
          </b-form-group>
          </b-col>

          <b-col md="12">
            <b-form-group :label="`${$t('Geofence')} (optional)`">
              <v-select multiple :close-on-select="false" v-model="request.geofences"
                        :dir="$store.getters['auth/preferDirIsRtl'] ? 'rtl' : 'ltr'"
                        :getOptionLabel="g => g.name"
                        :options="[...geofenceOptions]"
                        :reduce="g => g.geofence_id" />
            </b-form-group>
          </b-col>

          <b-col md="12">
            <b-form-group>
              <h5>{{ $t('Range Datetime')}}</h5>

              <flat-pickr v-model="rangeDate" class="form-control" :config="{...conf }"/>
              <small v-if="errors && (('from' in errors) || ('to' in errors))" class="text-danger">
                {{ $t(errors['from'][0]) }} {{ $t(errors['to'][0]) }}
              </small>
            </b-form-group>
          </b-col>

          <b-col  cols="12">
            <b-button type="submit" variant="primary" class="mr-1">
              {{ $t('Get Report') }}
            </b-button>
          </b-col>

        </b-row>

      </b-form>
    </b-card>


    <!--   report table-->
    <b-card v-if="reportFirstTime">
      <b-row>
        <div v-if="loading" class="loading_page">
          <orbit-spinner :animation-duration="1000" :size="40" :color="'#0e96b1'" />
        </div>

        <b-col cols="12" class="mb-1 mb-md-1">
          <v-select
              v-model="request.perPage"
              :options="[25,50,100,500]"
              :clearable="false"
              @change="handlePerPageChange"
              class="per-page-selector d-inline-block mx-50" />
        </b-col>

        <b-col cols="12" class="">
          <b-button :disabled="result.length < 1 || export_status"
                    @click="exportExcel('xlsx')"
                    size="sm"
                    variant="primary"
                    class="mr-1 mb-1">
            {{ $t('Excel Export') }}
          </b-button>

          <b-button :disabled="result.length < 1 || export_status"
                    @click="exportExcel('pdf')"
                    size="sm"
                    variant="primary"
                    class="mr-1 mb-1">
            {{ $t('PDF Export') }}
          </b-button>
        </b-col>

        <b-col cols="12" class="mb-1 mb-md-1">
          <b-table ref="myTable" responsive small bordered hover no-local-sorting
                   @sort-changed="sorted" :fields="[{key: 'i', label: '#'},...fields]"
                   :empty-text="$t('There are no records to show')"
                   :items="result.data">

            <template #cell(i)="data">
              {{ data.index + 1 }}
            </template>

            <template #head()="data">
              <span class="text-info">{{ $t(data.label) }}</span>
            </template>

          </b-table>
        </b-col>

        <b-col cols="12" class="mb-1 mb-md-1">
          <div class="mb-1">
            <span class="text-muted">{{ $t('Showing') }}
                  {{ result.from }} {{$t('to')}}} {{ result.to }} {{ $t('of') }} {{ result.total }} {{$t('entries')}}
            </span>
          </div>
          <b-pagination v-model="result.current_page"
                        :total-rows="result.total"
                        :per-page="result.per_page"
                        @change="handlePageChange"
                        aria-controls="my-table">
          </b-pagination>
        </b-col>
      </b-row>
    </b-card>

  </div>

</template>

<script>

import {
  BButton, BTable, BFormInvalidFeedback, BCard, BCol, BFormCheckboxGroup,
  BForm, BFormCheckbox, BFormRadioGroup, BFormGroup, BFormInput, BPagination, BCardTitle, BCardHeader,
  BInputGroup, BInputGroupPrepend, BRow, BAlert, BTabs, BTab, BCardText, BLink, BMedia,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import vSelect from 'vue-select'
import { OrbitSpinner } from 'epic-spinners'
import { computed, onMounted, reactive, ref, watch } from '@vue/composition-api'
import axios from '@/libs/axios'
import store from '@/store'
import fields from '@/views/reports/PTO/fields'
import { conf, rangeDate, from, to } from '@/views/reports/PTO/datepicker'
import { createToastInterface } from "vue-toastification"
import '@core/scss/vue/libs/toastification.scss'


export default {
  components: {
    BRow, BCard, BFormCheckboxGroup, BTable, BPagination,
    BCol, BFormRadioGroup, BFormInvalidFeedback,
    BAlert, BFormGroup, BFormInput, flatPickr,
    BFormCheckbox, BInputGroup,
    BInputGroupPrepend,
    BForm,
    BButton,
    BTabs,
    BCardText,
    BTab,
    vSelect,
    OrbitSpinner,
    BLink,
    BMedia,
    BCardTitle, BCardHeader,
  },

  setup() {

    const myTable = ref(null)
    const currentPage = ref(1)

    const reportFirstTime = ref(false)

    const perPage = ref(25)
    const sortBy = ref('on_time')
    const sortDesc = ref(false)

    const result = ref([]);
    const errors = ref([]);

    const export_status = ref(false)

    const request = reactive({
      compactors: [],
      geofences: [],
      owners: [],
      contracts: [],
      from: from,
      to: to,
      perPage: 25,
      page: 1,
      orderBy: {
        sortBy: 'on_time',
        sortDesc: false
      },

    })

    const pluginOptions = {
      position: "bottom-right",
      timeout: false,
      closeOnClick: false,
      pauseOnFocusLoss: true,
      pauseOnHover: true,
      draggable: false,
      draggablePercent: 0.12,
      showCloseButtonOnHover: false,
      hideProgressBar: true,
      closeButton: false,
      icon: true,
      rtl: false
    }

    const toast = createToastInterface(pluginOptions)

    const loading = ref(false)
    const compactors = ref([])
    const geofences = ref([])

    const selectedOwners = ref([])
    const selectedContracts = ref([])

    const contractsOptions = computed(() => {
      return contractsFilter.value.filter(i =>
          selectedOwners.value.includes(i.contractor_id))
    })

    const compactorsOptions = computed(() => {
      return compactors.value.filter(i =>
          (selectedOwners.value.length === 0 && selectedContracts.value.length === 0 )||
          (selectedOwners.value.includes(i.owner_id) ||
          selectedContracts.value.includes(i.contract_id)))
    })


    const geofenceOptions = computed(() => {
      return geofences.value.filter(i =>
          (selectedOwners.value.length === 0 && selectedContracts.value.length === 0 )||
          (selectedOwners.value.includes(i.owner_id) ||
              selectedContracts.value.includes(i.contract_id)))
    })

    watch([selectedOwners, selectedContracts], () => {
      request.contracts = selectedContracts.value
      request.owners = selectedOwners.value
    })

    watch([perPage], () => {
      request.perPage = perPage.value
      submitReport()
    })


    const getCompactors = _ => {
      axios.get('userGetLinked', {params: {
          user: store.state.auth.userData.id,
          type: 'device',
          cols: 'device_id,name,owner_id,contract_id',
          category: 'COMPACTOR',
        }}).then(res => compactors.value = res.data.data).catch(e => {})
    }

    const getGeofence = _ => {

      axios.get('userGetLinked', { params: {
          user: store.state.auth.userData.id,
          type: 'geofence',
          cols: 'geofence_id,name,owner_id,contract_id',
          category: 'WORK_AREA'
        }}).then(res => geofences.value = res.data.data).catch(e => {})
    }

    getCompactors()
    getGeofence()

    const ownersFilter =  ref([])
    const contractsFilter =  ref([])

    axios.get('filters/getFilterBy', { params: {
        type: 'device', category: 'COMPACTOR', }})

    .then(res => {
      ownersFilter.value = res.data.data.owners
      contractsFilter.value = res.data.data.contracts
    })

    .catch(e => {})


    const sorted =  ctx => {
      request.orderBy.sortBy = ctx.sortBy
      request.orderBy.sortDesc = ctx.sortDesc
      submitReport()
    }

    const handlePageChange = page => {
      request.page = page
      submitReport()
    }


    const handlePerPageChange = page => {
      console.log(page)
    }


    const submitReport = () => {

      loading.value = true
      errors.value = []

     axios.post('reports', { type: 'PTO', ...request })
     .then(res => {
       result.value = res.data.data
       reportFirstTime.value = true
     }).catch(e => {
       if (e.response.status === 422) {
         errors.value = e.response.data.errors
       }
     }).finally(_ => loading.value = false)

    }


    const exportExcel = type => {
      toast.info('Report Export Processing ...')
      export_status.value = true
      axios.post(
          'export',
          { exporter: type, export: "PTOReport", ...request },
          {responseType: 'blob'}
      )

      .then(res => {
        const blob = new Blob(
            [res.data],
            { type: res.headers['content-type'], encoding: 'UTF-8' }
        );
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `PTO.${type}`;
        link.click();
        URL.revokeObjectURL(url);
      })

      .catch(e => console.log('error export report'))
      .finally(_ => {
        export_status.value = false
        toast.clear()
      })

    }



    return {

      myTable,
      currentPage,
      perPage,
      sortBy, sortDesc,
      fields,conf, rangeDate,

      result, errors, reportFirstTime,
      loading, export_status,
      compactors, geofences,

      // filters
      ownersFilter, contractsFilter,
      selectedOwners, selectedContracts,
      contractsOptions,

      // computed
      compactorsOptions,geofenceOptions,

      request,
      submitReport,
      exportExcel, sorted,
      handlePageChange,
      handlePerPageChange,
    }
  }
}

</script>

<style scoped lang="scss">

</style>